<template>
  <b-modal ref="dish-view-modal"
           hide-footer
           scrollable
           size="lg"
           :title="$t('#TR-dish-view')">

    <div v-if="viewLoader" class="small-loader">
      <i class="mdi mdi-rotate-right mdi-spin"></i>
    </div>

    <div v-if="dish">

      <table class="table table-bordered table-condensed small">
        <tbody>
          <tr>
            <td class="font-weight-bold">{{ $t("#TR-name") }}</td>
            <td> {{ dish['name'] }}</td>
          </tr>
          <tr>
            <td class="font-weight-bold">{{ $t("#TR-code") }}</td>
            <td> {{ dish['code'] }}</td>
          </tr>
          <tr>
            <td class="font-weight-bold">{{ $t("#TR-external-code") }}</td>
            <td> {{ dish['extCode'] }}</td>
          </tr>
          <tr>
            <td class="font-weight-bold">{{ $t("#TR-modischeme") }}</td>
            <td> {{ dish['_modiScheme'] ? dish['_modiScheme']['name'] : '&mdash;' }}</td>
          </tr>
          <tr>
            <td class="font-weight-bold">{{ $t("#TR-fiscal-group") }}</td>
            <td> {{ dish['_taxDishType'] ? dish['_taxDishType']['name'] : '&mdash;' }}</td>
          </tr>
          <tr>
            <td class="font-weight-bold">{{ $t("#TR-prices") }}</td>
            <td>
              <table v-if="dish.prices.length > 0" class="table table-borderless table-striped">
                <thead>
                  <tr class="font-weight-bolder text-muted">
                    <th class="p-1 w-50">{{ $t("#TR-type") }}</th>
                    <th class="p-1">{{ $t("#TR-value") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(price, priceIdx) in dish['prices']"
                      :key="priceIdx">
                    <td class="p-1">{{ price.type.name }}</td>
                    <td class="p-1">{{ price.value }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-if="dish.prices.length < 1">&mdash;</div>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">{{ $t("#TR-classifications") }}</td>
            <td>
              <table v-if="dish.classifications.length >0"
                     class="table table-borderless table-striped mb-0">
                <thead>
                  <tr class="font-weight-bolder text-muted">
                    <th class="p-1 w-50">{{ $t("#TR-type") }}</th>
                    <th class="p-1">{{ $t("#TR-category") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(classification, classificationIdx) in dish['classifications']"
                      :key="classificationIdx">
                    <td class="p-1">{{ classification.type.name }}</td>
                    <td class="p-1">{{ classification.cat.name }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-if="dish.classifications.length < 1">&mdash;</div>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">{{ $t('#TR-barcodes') }}</td>
            <td>
              <table v-if="dish.barcodes.length >0"
                     class="table table-borderless table-striped mb-0">
                <tr v-for="(barcode, i) in dish.barcodes"
                    :key="i">
                  <td class="p-1">{{ barcode }}</td>
                </tr>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="row">
        <div class="col-12 text-center">
          <b-button variant="danger"
                    type="button"
                    class="mx-1"
                    @click="cancel">
            <span class="mdi mdi-close"></span> {{ $t("#TR-cancel") }}
          </b-button>
          <b-button variant="primary"
                    type="button"
                    class="mx-1"
                    @click="onEdit()">
            <span class="mdi mdi-pencil"></span> {{ $t("#TR-edit") }}
          </b-button>
        </div>
      </div>

    </div>
  </b-modal>
</template>

<script>

  import { ApiDish, ApiRefEditor } from '@/services/api';
  import MSG from '@/services/notify';
  import { EditorRefs } from '@/services/EditorRefs';

  export default {
    name: 'DishViewModal',

    props: {
      viewedDish: {
        default: () => {
          return null;
        },
        type: Object
      }
    },

    data() {
      return {
        dish: null,
        viewLoader: false,
        refs: {
          TaxDishTypes: [],
          Classifications: [],
          ModiSchemes: [],
          PriceTypes: []
        }
      };
    },

    methods: {

      filteredEvents(arr) {
        return arr.filter(item => {
          return item.value || (item.value === 0);
        });
      },


      async getDish(dish) {
        this.viewLoader = true;
        const result = await ApiDish.get(dish.guid);

        if (result.error) {
          MSG.error(result.error, this);
          this.viewLoader = false;
          this.cancel();
        } else {
          await this.getRefs();
          this.dish = await this.prepareDish(result.data);
          this.viewLoader = false;
        }
      },

      async getRefs() {
        this.refs = await EditorRefs.getRefs([
          'TaxDishTypes',
          'Classifications',
          'ModiSchemes',
          'PriceTypes'
        ], this);
      },

      async prepareDish(dish) {
        dish._modiScheme = EditorRefs.findElement(this.refs.ModiSchemes, 'sifr', dish.modiScheme);
        dish._taxDishType = EditorRefs.findElement(this.refs.TaxDishTypes, 'sifr', dish.taxDishType);

        dish.prices.forEach(p => {
          p.type = EditorRefs.findElement(this.refs.PriceTypes, 'sifr', p.sifr);
        });

        dish.classifications.forEach(c => {
          c.type = EditorRefs.findElement(this.refs.Classifications, 'sifr', c.sifr);
        });

        dish.prices = this.filteredEvents(dish.prices);
        dish.classifications = this.filteredEvents(dish.classifications);

        await this.prepareCats(dish);

        return dish;
      },

      onEdit() {
        this.$router.push({
          name: 'dishesEdit',
          params: {
            guid: this.dish.guid,
            group: this.dish.parent
          }
        }).catch(() => {});
      },
      cancel() {
        this.$refs['dish-view-modal'].hide();
        this.$emit('cancelView');
      },

      async openDishView(dish) {
        this.$refs['dish-view-modal'].show();
        await this.getDish(dish);
      },

      async prepareCats(dish) {

        for (const c of dish.classifications) {

          const result = await ApiRefEditor.getClassificationsCategories(c.sifr);
          if (result.error) {
            MSG.error(result.error, this);
          } else {
            c.cat = EditorRefs.findElement(result.data, 'sifr', c.value);
          }
        }
      }
    }
  };
</script>

<style lang="stylus" scoped>

.view-dish {
    .mdi {
        font-size: 24px
        line-height: 24px
    }
}


</style>
